import {
  AfterContentChecked,
  ChangeDetectorRef,
  computed,
  ContentChildren,
  Directive,
  forwardRef,
  inject,
  Input,
  model,
  QueryList
} from '@angular/core';
import { TabsetItemDirectiveToken } from '../tokens/tabset-item.directive.token';
import { TabsetDirectiveToken, TabsetDirectiveType } from '../tokens/tabset.directive.token';
import { TabsetContentDirective } from './tabset-content.directive';

/**
 * The directive used to group Tab link and related Tab content.
 * As well as set Tab identifier and some options.
 */
@Directive({
  selector: '[mbsTabsetItem]',
  exportAs: 'mbsTabsetItem',
  host: {
    '[class.mbs-tabset_item]': '!nested',
    '[class.mbs-tabset_group-item]': 'nested',
    '[class.-active]': 'isActiveAndNotDisabled',
    '[class.-disabled]': 'isDisabled',
    '[class.-loading]': 'isLoading() || isForceLoading'
  },
  providers: [{ provide: TabsetItemDirectiveToken, useExisting: forwardRef(() => TabsetItemDirective) }]
})
export class TabsetItemDirective implements AfterContentChecked {
  parentTabset = inject<TabsetDirectiveType>(TabsetDirectiveToken, { optional: true });
  parentTab = inject(TabsetItemDirective, { skipSelf: true, optional: true });
  private cdRef = inject(ChangeDetectorRef);

  public nested = false;
  public forceDisabled = false;
  public forceLoading = false;
  public contentTemplate: TabsetContentDirective;

  @Input() disabled: boolean;
  loading = model<boolean>(false);
  @Input() fullHeight: boolean;
  @Input() loadOnAppearance: boolean;
  @Input('mbsTabsetItem') id: string;
  @Input() linkId: string;

  @Input() set active(isActive: boolean) {
    isActive && this.activateSelf();
  }

  @ContentChildren(TabsetContentDirective, { descendants: false }) contentDirectives: QueryList<TabsetContentDirective>;
  @ContentChildren(forwardRef(() => TabsetItemDirective), { descendants: true }) nestedItems: QueryList<TabsetItemDirective>;

  public isLoading = computed(() => this.loading() || (this.parentTab && this.parentTab.loading()));

  get isActiveAndNotDisabled(): boolean {
    return this.isActive ? !this.disabled && !this.forceDisabled : false;
  }

  get isActive(): boolean {
    if (this.id === this.parentTabset.activeId && !this.parentTab?.loading()) return true;

    const nested = this.nestedItems.toArray();

    return nested.length > 0 && !!nested.find((item) => item.id === this.parentTabset.activeId);
  }

  get isDisabled(): boolean {
    return this.disabled || this.forceDisabled || (this.parentTab && this.parentTab.isDisabled);
  }

  get isForceLoading(): boolean {
    return this.forceLoading || (this.parentTab && this.parentTab.forceLoading);
  }

  constructor() {
    this.nested = this.parentTab != null;

    if (this.loadOnAppearance == null) {
      this.loadOnAppearance = this.parentTabset.loadOnAppearance;
    }
  }

  detectChanges(): void {
    this.cdRef.detectChanges();
  }

  activateSelf(): void {
    this.parentTabset.select(this.id);
  }

  isPanelInDom(): boolean {
    return (
      (this.loadOnAppearance == null ? !this.loadOnAppearance : !this.parentTabset.loadOnAppearance) ||
      (this.isActive && !this.nested && this.nestedItems.toArray().length === 0) ||
      (this.isActive && this.parentTab != null)
    );
  }

  ngAfterContentChecked(): void {
    // We are using @ContentChildren instead of @ContentChild as in the Angular version being used
    // only @ContentChildren allows us to specify the {descendants: false} option.
    // Without {descendants: false} we are hitting bugs described in:
    // https://github.com/ng-bootstrap/ng-bootstrap/issues/2240
    this.contentTemplate = this.contentDirectives.first;
  }
}
